import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  PiggyBank,
  SearchSlash,
  TextSearch,
} from "lucide-react";

import * as Yup from "yup";

import {
  MultiSelect,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
  Label,
  Skeleton,
  PageHeader,
} from "@/components/ui";
import { Button } from "@/components/ui/button";
import {
  BillingPaymentDataTableReport,
  FinanceCard,
} from "@/components/common";
import {
  useDownloadReportEntriesToExcelMutation,
  useDownloadReportEntriesToPDFMutation,
  useFetchEmployeesNoPaginateQuery,
  // useFetchEmployeesNoPaginateQuery,
  useFetchFinanceAccountsQuery,
  useFetchFinancePatientReportQuery,
  useFetchPatientsNoPaginateQuery,
  useRequestFinancialPatientsPDFMutation,
  useToast,
} from "@/hooks";

import { endOfMonth, format, isValid, parse, startOfMonth } from "date-fns";
import { formatDateToBackend } from "@/utils";
import { defaultFinancialAccounts } from "@/helpers";

interface HandleFilterReportFinanceProps {
  status: string;
  patientId: string;
  employeeId: string;
  startDate: string | null;
  endDate: string;
  paymentMethod: string;
  accountId: string;
}

export function EntriesReports() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const today = new Date();
  const firstDayOfMonth = startOfMonth(today);
  const lastDayOfMonth = endOfMonth(today);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [searchParams, setSearchParams] = useSearchParams();

  const page = searchParams.get("page");
  const accountId = searchParams.get("accountId");
  const perPageParam = searchParams.get("per_page");
  const dateStart = searchParams.get("startDate");
  const dateEnd = searchParams.get("endDate");
  const status = searchParams.get("status");
  const patientsId = searchParams.get("patientId");
  const employeesId = searchParams.get("employeeId");
  const paymentMethod = searchParams.get("paymentMethod");

  const [perPage, setPerPage] = useState<number>(
    perPageParam ? Number(perPageParam) : 10
  );

  const { mutateAsync: downloadExcel, isLoading: isLoadingDownloadExcel } =
    useDownloadReportEntriesToExcelMutation({
      page: page || "1",
      startDate: dateStart || "",
      endDate: dateEnd || "",
      status: status || "",
      patientId: patientsId || "",
      employeeId: employeesId || "",
      paymentMethod: paymentMethod || "",
      accountId: accountId || "",
    });
  const { mutateAsync: downloadPDF, isLoading: isLoadingDownloadPDF } =
    useDownloadReportEntriesToPDFMutation({
      page: page || "1",
      startDate: dateStart || "",
      endDate: dateEnd || "",
      status: status || "",
      patientId: patientsId || "",
      employeeId: employeesId || "",
      paymentMethod: paymentMethod || "",
      accountId: accountId || ""
    });
  const { data: dataAccounts, isLoading: isLoadingAccounts } =
    useFetchFinanceAccountsQuery({ per_page: 50 });

  const { mutateAsync: requestPDF, isLoading: isLoadingRequestPDF } = useRequestFinancialPatientsPDFMutation();

  const {
    data: patientsReports,
    isLoading: isLoadingPatientsReports,
    refetch,
  } = useFetchFinancePatientReportQuery({
    page: page || "1",
    startDate: dateStart || "",
    endDate: dateEnd || "",
    status: status || "",
    patientId: patientsId || "",
    employeeId: employeesId || "",
    paymentMethod: paymentMethod || "",
    accountId: accountId || "",
    per_page: perPage || 10,
  });

  const { data: patientsData, isLoading: isLoadingPatients } =
    useFetchPatientsNoPaginateQuery();
  const { data: employeesData, isLoading: isLoadingEmployees } =
    useFetchEmployeesNoPaginateQuery();

  const initialValues: HandleFilterReportFinanceProps = {
    status: "",
    patientId: "",
    employeeId: "",
    startDate: format(firstDayOfMonth, 'dd/MM/yyyy') || "",
    endDate: format(lastDayOfMonth, 'dd/MM/yyyy') || "",
    paymentMethod: "",
    accountId: accountId || "",
  };

  const { handleSubmit, resetForm, ...formik } =
    useFormik<HandleFilterReportFinanceProps>({
      initialValues,
      validationSchema: Yup.object().shape({
        status: Yup.string().optional(),
        patientId: Yup.string().optional(),
        procedureId: Yup.string().optional(),
        categoryId: Yup.string().optional(),
        startDate: Yup.date()
          .transform(function (value, originalValue) {
            if (this.isType(value)) {
              return value;
            }

            let parsedDate = parse(originalValue, "dd.MM.yyyy", new Date());
            if (isValid(parsedDate)) {
              return parsedDate;
            }

            parsedDate = parse(originalValue, "dd/MM/yyyy", new Date());
            if (isValid(parsedDate)) {
              return parsedDate;
            }

            return new Date("");
          })
          .typeError(t("LABEL.invalid date"))
          .optional(),
        endDate: Yup.date()
          .transform(function (value, originalValue) {
            if (this.isType(value)) {
              return value;
            }

            let parsedDate = parse(originalValue, "dd.MM.yyyy", new Date());
            if (isValid(parsedDate)) {
              return parsedDate;
            }

            parsedDate = parse(originalValue, "dd/MM/yyyy", new Date());
            if (isValid(parsedDate)) {
              return parsedDate;
            }

            return new Date("");
          })
          .typeError(t("LABEL.invalid date"))
          .optional(),
      }),

      onSubmit: async (data) => {
        await handlefilterFinance(data);
      },
    });

  async function handlefilterFinance(values: HandleFilterReportFinanceProps) {
    try {
      if (values.paymentMethod)
        queryParams.set("paymentMethod", values.paymentMethod);
      if (values.accountId)
        queryParams.set("accountId", values.accountId);
      if (values.startDate)
        queryParams.set("startDate", formatDateToBackend(values.startDate));
      if (values.endDate)
        queryParams.set("endDate", formatDateToBackend(values.endDate));
      if (values.status) queryParams.set("status", values.status);
      if (values.patientId) queryParams.set("patientId", values.patientId);
      if (values.employeeId) queryParams.set("employeeId", values.employeeId);
      await navigate({ search: queryParams.toString() });

    } catch {
      toast({
        title: t("LABEL.error"),
        description: t("MESSAGE.error"),
        variant: "destructive",
      });
    }
  }

  function clearFilter() {
    queryParams.delete("status");
    queryParams.delete("accountId");
    queryParams.delete("patientId");
    queryParams.delete("employeeId");
    queryParams.delete("paymentMethod");
    queryParams.delete("startDate");
    queryParams.delete("endDate");
    queryParams.delete("per_page");

    if (formik.values.startDate)
      queryParams.set("startDate", formatDateToBackend(formik.values.startDate));
    if (formik.values.endDate)
      queryParams.set("endDate", formatDateToBackend(formik.values.endDate));

    navigate({ search: queryParams.toString() });
    resetForm();
  }

  function hasFiltersApplied() {
    const relevantParams = [
      "accountId",
      "status",
      "patientId",
      "employeeId",
      "paymentMethod",
      "startDate",
      "endDate",
    ];
    return (
      Object.values(formik.values).some((value) => value) ||
      relevantParams.some((param) => queryParams.has(param))
    );
  }

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("per_page", newPerPage.toString());
    setSearchParams(newSearchParams);
  };

  const handlePrint = async () => {
    try {
      const blob = await requestPDF({
        page: page || "1",
        startDate: dateStart || "",
        endDate: dateEnd || "",
        status: status || "",
        patientId: patientsId || "",
        employeeId: employeesId || "",
        paymentMethod: paymentMethod || "",
        accountId: accountId || "",
        per_page: perPage || 10,
      });
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;

      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            document.body.removeChild(iframe);
          }, 1000);
        }, 500);
      };
    } catch (error) {
      console.error("Erro ao gerar PDF para impressão:", error);
    }
  };

  useEffect(() => {
    const shouldApplyDefaultDateFilter = !dateStart && !dateEnd;

    if (shouldApplyDefaultDateFilter) {
      handlefilterFinance(initialValues);
    }

  }, []);

  useEffect(() => {
    refetch();
  }, [
    refetch,
    page,
    dateStart,
    dateEnd,
    status,
    patientsId,
    employeesId,
    paymentMethod,
    accountId,
    perPageParam,
  ]);

  return (
    <section className="space-y-7">
      <PageHeader eyebrow={t("LABEL.reports")} title={t("LABEL.financial entries report")} />
      <main className="space-y-14">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-4 gap-x-2 gap-y-4">
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.from")}</Label>
              <DatePicker
                {...formik.getFieldProps("startDate")}
                isInvalid={
                  formik.touched.startDate && !!formik.errors.startDate
                }
                isValid={formik.touched.startDate && !formik.errors.startDate}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.to")}</Label>
              <DatePicker
                {...formik.getFieldProps("endDate")}
                isInvalid={formik.touched.endDate && !!formik.errors.endDate}
                isValid={formik.touched.endDate && !formik.errors.endDate}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.status")}</Label>
              <Select
                onValueChange={(value) => {
                  formik.setFieldValue("status", value);
                }}
                {...formik.getFieldProps("status")}
                defaultValue={formik.values.status}
                key={formik.values.status}
              >
                <SelectTrigger
                  isInvalid={
                    formik.touched.status && formik.errors.status !== undefined
                  }
                  isValid={
                    formik.touched.status && formik.errors.status === undefined
                  }
                >
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">{t("LABEL.all")}</SelectItem>
                    <SelectItem value="open">
                      {t("LABEL.opens")}
                    </SelectItem>
                    <SelectItem value="pay">
                      {t("LABEL.paids")}
                    </SelectItem>
                    <SelectItem value="delayed">
                      {t("LABEL.delayeds")}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.collaborator")}</Label>
              <MultiSelect
                options={[
                  { value: "all", label: t("LABEL.all") },
                  ...(employeesData?.map((employee) => ({
                    value: employee.id,
                    label: employee.person.name,
                  })) || []),
                ]}
                loading={isLoadingEmployees}
                placeholder="Selecione um colaborador..."
                width="300px"
                isInvalid={
                  formik.touched.employeeId && formik.errors.employeeId
                }
                isValid={formik.touched.employeeId && !formik.errors.employeeId}
                value={formik.values.employeeId}
                onChange={(value) => formik.setFieldValue("employeeId", value)}
                onBlur={() => formik.handleBlur("employeeId")}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.patient")}</Label>
              <MultiSelect
                options={[
                  { value: "all", label: t("LABEL.all") },
                  ...(patientsData?.map((patient) => ({
                    value: patient.id,
                    label: patient.person.name,
                  })) || []),
                ]}
                loading={isLoadingPatients}
                placeholder="Selecione um paciente..."
                width="300px"
                isInvalid={formik.touched.patientId && formik.errors.patientId}
                isValid={formik.touched.patientId && !formik.errors.patientId}
                value={formik.values.patientId}
                onChange={(value) => formik.setFieldValue("patientId", value)}
                onBlur={() => formik.handleBlur("patientId")}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.payment method")}</Label>
              <Select
                {...formik.getFieldProps("paymentMethod")}
                onValueChange={(value) => {
                  formik.setFieldValue("paymentMethod", value);
                }}
                defaultValue={formik.values.paymentMethod}
                key={formik.values.paymentMethod}
              >
                <SelectTrigger
                  isInvalid={
                    formik.touched.paymentMethod &&
                    formik.errors.paymentMethod !== undefined
                  }
                  isValid={
                    formik.touched.paymentMethod &&
                    formik.errors.paymentMethod === undefined
                  }
                >
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent defaultValue="all">
                  <SelectGroup>
                    <SelectItem value="all">{t("LABEL.all")}</SelectItem>
                    <SelectItem value="Pix">{t("LABEL.pix")}</SelectItem>
                    <SelectItem value="Money">{t("LABEL.money")}</SelectItem>
                    <SelectItem value="CreditCard">Cartão de crédito</SelectItem>
                    <SelectItem value="DebitCard">Cartão de débito</SelectItem>
                    <SelectItem value="Ticket">{t("LABEL.ticket")}</SelectItem>
                    <SelectItem value="AutomaticDebit">
                      {t("LABEL.automatic debit")}
                    </SelectItem>
                    <SelectItem value="BankDeposit">
                      {t("LABEL.bank deposit")}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.account")}</Label>
              <MultiSelect
                options={[
                  { value: "all", label: t("LABEL.all") },
                  ...(dataAccounts?.items?.map((account) => ({
                    value: account.id,
                    label: defaultFinancialAccounts.includes(account.name) ? t(account.name.toLowerCase()) : account.name,
                  })) || []),
                ]}
                loading={isLoadingAccounts}
                placeholder="Selecione uma conta..."
                isInvalid={
                  formik.touched.accountId && formik.errors.accountId
                }
                isValid={formik.touched.accountId && !formik.errors.accountId}
                value={formik.values.accountId}
                onChange={(value) => formik.setFieldValue("accountId", value)}
                onBlur={() => formik.handleBlur("accountId")}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button className="items-center px-10 h-10">
                <TextSearch size={14} /> <span>{t("LABEL.consult")}</span>
              </Button>
              {hasFiltersApplied() && (
                <Button type="button" onClick={clearFilter} variant="secondary">
                  <SearchSlash size={14} /> <span>{t("LABEL.to clean")}</span>
                </Button>
              )}
            </div>
          </div>
        </form>

        {patientsReports && (
          <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FinanceCard
              icon={PiggyBank}
              iconBgColor="bg-green-200"
              iconTextColor="text-green-700"
              label={t("LABEL.paids")}
              realizedValue={patientsReports.pay}
            />
            <FinanceCard
              icon={ArrowDownCircle}
              iconBgColor="bg-red-200"
              iconTextColor="text-red-700"
              label={t("LABEL.delayeds")}
              realizedValue={patientsReports.delayed}
            />
            <FinanceCard
              icon={ArrowUpCircle}
              iconBgColor="bg-blue-200"
              iconTextColor="text-blue-700"
              label={t("LABEL.opens")}
              realizedValue={patientsReports.pay}
            />
          </section>
        )}

        {!isLoadingPatientsReports && patientsReports ? (
          <div className="space-y-3">
            <div className="flex items-center justify-end gap-x-2">
              <Button
                variant="secondary"
                onClick={handlePrint}
                isLoading={isLoadingRequestPDF}>
                {t("LABEL.print")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => downloadExcel()}
                isLoading={isLoadingDownloadExcel}
              >
                {t("LABEL.export excel")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => downloadPDF()}
                isLoading={isLoadingDownloadPDF}
              >
                {t("LABEL.export pdf")}
              </Button>
            </div>
            <BillingPaymentDataTableReport handlePerPageChange={handlePerPageChange} perPage={perPage} data={patientsReports} />
          </div>
        ) : (
          <div>
            <Skeleton className="w-full h-[500px]" />
          </div>
        )}
      </main>
    </section>
  );
}
